import { Timestamp, timestamp } from '@fullstory/proto/google/protobuf';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import {
  createRequestCancellationAction,
  RequestDispatch,
  createGRPCAction,
  RequestActionType,
} from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { JourneyComputationRequestBody, JourneysRequestTypes } from './types';
import { getCacheOptions, metricsCacheOptionsRefresh } from '../metrics/utils';
import { AppDispatch } from '../../types';
import { ActionType } from '@fullstory/redux-util/utils/types';
import { createAppCacheActions } from '@fullstory/redux-util/utils/appCache';
import {
  ComputeJourneyMapResponse,
  PageMetricsServicePublic,
} from '@fullstory/proto/fs/services/alexa';
import { Flags } from '@fullstory/client-api';
import { isDataFresh } from '@fullstory/searchiecache';
import { journeyMapRequestBodyToComputeJourneyMapRequest, journeyMapSimilarData } from './utils';
import { hashObject } from '@fullstory/util/hash';
import { BatchOptions } from '@fullstory/util/xhr';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import * as dxo from '@fullstory/proto/fs/services/dx-objects';
import { UID } from '@fullstory/util/uid';

const makeGetAllJourneysRequest = (orgId: string, includeArchived: boolean) => ({
  orgId,
  includeArchived,
});
const cacheKeysToInvalidate = (orgId: string) => {
  const endpoint = dxo.JourneysPublic.GetAllJourneysEndpoint;

  const getAllJourneysRequest = makeGetAllJourneysRequest(orgId, true);
  const getActiveJourneysRequest = makeGetAllJourneysRequest(orgId, false);
  return [
    defaultCacheKey(endpoint, getAllJourneysRequest),
    defaultCacheKey(endpoint, getActiveJourneysRequest),
  ];
};

export const JourneysRequestActions = {
  getAllJourneys: (body: dxo.GetAllJourneysRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.GetAllJourneys>()(
      {
        type: JourneysRequestTypes.GetAllJourneysRequest,
        grpcFunction: dxo.JourneysPublic.GetAllJourneys,
        body,
        readWrite: 'write',
      },
      JourneysRequestTypes.GetAllJourneysSuccess,
      JourneysRequestTypes.GetAllJourneysFailure,
    ),
  setJourney: (body: dxo.SetJourneyRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.SetJourney>()(
      {
        type: JourneysRequestTypes.SetJourneyRequest,
        grpcFunction: dxo.JourneysPublic.SetJourney,
        body,
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      JourneysRequestTypes.SetJourneySuccess,
      JourneysRequestTypes.SetJourneyFailure,
    ),
  markJourneyViewed: (body: dxo.MarkJourneyViewedRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.MarkJourneyViewed, { lastViewed: timestamp }>({
      modify: () => ({ lastViewed: Timestamp.now() }),
    })(
      {
        type: JourneysRequestTypes.MarkJourneyViewedRequest,
        grpcFunction: dxo.JourneysPublic.MarkJourneyViewed,
        body,
        force: true,
        readWrite: 'write',
      },
      JourneysRequestTypes.MarkJourneyViewedSuccess,
      JourneysRequestTypes.MarkJourneyViewedFailure,
    ),
  starJourney: (body: dxo.StarJourneyRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.StarJourney>()(
      {
        type: JourneysRequestTypes.StarJourneyRequest,
        grpcFunction: dxo.JourneysPublic.StarJourney,
        body,
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      JourneysRequestTypes.StarJourneySuccess,
      JourneysRequestTypes.StarJourneyFailure,
    ),
  archiveJourney: (body: dxo.ArchiveJourneyRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.ArchiveJourney>()(
      {
        type: JourneysRequestTypes.ArchiveJourneyRequest,
        grpcFunction: dxo.JourneysPublic.ArchiveJourney,
        body,
        force: true,
        invalidateCacheKey: cacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      JourneysRequestTypes.ArchiveJourneySuccess,
      JourneysRequestTypes.ArchiveJourneyFailure,
    ),
  getAccess: (body: CanAccessRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.GetJourneysAccess>()(
      {
        type: JourneysRequestTypes.GetJourneyAccessRequest,
        grpcFunction: dxo.JourneysPublic.GetJourneysAccess,
        body,
        readWrite: 'read',
      },
      JourneysRequestTypes.GetJourneyAccessSuccess,
      JourneysRequestTypes.GetJourneyAccessFailure,
    ),
  computeJourneyMap: (
    body: JourneyComputationRequestBody,
    uid?: UID,
    batchOptions?: BatchOptions,
  ) => {
    const requestBody = journeyMapRequestBodyToComputeJourneyMapRequest(body);
    return createGRPCAction<typeof PageMetricsServicePublic.ComputeJourneyMap>({
      cacheKey: (url: string) => {
        const { cacheOptions, ...requestCacheData } = body;
        return `${url}:${hashObject(requestCacheData)}`;
      },
      uid,
    })(
      {
        type: JourneysRequestTypes.ComputeJourneyRequest,
        grpcFunction: PageMetricsServicePublic.ComputeJourneyMap,
        body: requestBody,
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
          batchOptions,
        },
        readWrite: 'write',
      },
      JourneysRequestTypes.ComputeJourneySuccess,
      JourneysRequestTypes.ComputeJourneyFailure,
    );
  },
  createUnnamedJourney: (body: dxo.CreateUnnamedJourneyRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.CreateUnnamedJourney>()(
      {
        type: JourneysRequestTypes.CreateUnnamedJourneyRequest,
        grpcFunction: dxo.JourneysPublic.CreateUnnamedJourney,
        body,
        readWrite: 'write',
      },
      JourneysRequestTypes.CreateUnnamedJourneySuccess,
      JourneysRequestTypes.CreateUnnamedJourneyFailure,
    ),
  getUnnamedJourney: (body: dxo.GetUnnamedJourneyRequest) =>
    createGRPCAction<typeof dxo.JourneysPublic.GetUnnamedJourney>()(
      {
        type: JourneysRequestTypes.GetUnnamedJourneyRequest,
        grpcFunction: dxo.JourneysPublic.GetUnnamedJourney,
        body,
        readWrite: 'read',
      },
      JourneysRequestTypes.GetUnnamedJourneySuccess,
      JourneysRequestTypes.GetUnnamedJourneyFailure,
    ),
};

export const JourneysActions = createAppCacheActions(
  JourneysRequestTypes.JourneysAppCacheIsLoading,
  JourneysRequestTypes.JourneysAppCacheLoadFailed,
  JourneysRequestTypes.JourneysAppCacheRefetching,
  JourneysRequestTypes.JourneysAppCacheHasData,
  JourneysRequestTypes.JourneysAppCacheFresh,
  JourneysRequestTypes.JourneysAppCacheSwap,
  JourneysRequestTypes.JourneysAppCacheClear,
)<ComputeJourneyMapResponse>();

export type JourneysActions =
  | RequestActionType<typeof JourneysRequestActions>
  | ActionType<typeof JourneysActions>;

export interface JourneysPayload {
  body: JourneyComputationRequestBody;
  flags: Flags;
}

const ensureCacheOptions = (body: JourneyComputationRequestBody) =>
  body.cacheOptions || getCacheOptions(body);

export const JourneysAsyncActions = {
  appCache: (uid?: UID, forceSwap?: boolean) => ({
    action: createAppCacheRequestAction<ComputeJourneyMapResponse, JourneysPayload>()({
      query: (
        dispatch: RequestDispatch<JourneysActions>,
        payload: JourneysPayload,
        isRefresh: boolean,
      ): Promise<ComputeJourneyMapResponse> => {
        const cacheOptions = isRefresh
          ? metricsCacheOptionsRefresh
          : ensureCacheOptions(payload.body);
        return dispatch(
          JourneysRequestActions.computeJourneyMap(
            {
              ...payload.body,
              cacheOptions,
            },
            uid,
          ),
        );
      },
      loading: JourneysActions.appCacheIsLoading,
      failed: JourneysActions.appCacheLoadFailed,
      refetching: JourneysActions.appCacheIsRefetching,
      hasData: JourneysActions.appCacheHasData,
      fresh: JourneysActions.appCacheFresh,
      isDataFresh: (payload: JourneysPayload, data: ComputeJourneyMapResponse) =>
        isDataFresh(
          ensureCacheOptions(payload.body),
          Timestamp.mustDate(data.computedAt),
          new Date(),
        ),
      isSimiliar: (newData: ComputeJourneyMapResponse, oldData: ComputeJourneyMapResponse) =>
        journeyMapSimilarData(newData.journeyMap, oldData.journeyMap),
      forceSwap,
    }),
    cancel: (dispatch: AppDispatch, uid: UID) => {
      dispatch(createRequestCancellationAction(uid));
    },
  }),
};
